// 화면 19 — 단일 일자 percentile 히스토그램 (P1).
// 권위: ARCH_DASHBOARD §4.19 / §6.2 — GET /api/v1/stocks/score-distribution/percentile?date=&bins=10.
//
// SVG bar chart — bin_start~bin_end 별 count.
// Y 축은 종목 수, X 축은 percentile bin (0 ~ 100, percent 단위).

import { memo, useMemo } from 'react';
import type { PercentileHistogramBin } from './types';

interface Props {
  bins: PercentileHistogramBin[];
  totalCount: number;
}

const VIEW_W = 760;
const VIEW_H = 240;
const PAD = { top: 12, right: 16, bottom: 36, left: 56 };

export const PercentileHistogramChart = memo(function PercentileHistogramChart({
  bins,
  totalCount,
}: Props): JSX.Element {
  const yMax = useMemo(() => {
    const m = bins.reduce((acc, b) => (b.count > acc ? b.count : acc), 0);
    return m === 0 ? 1 : m;
  }, [bins]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom;
  const colWidth = bins.length > 0 ? plotW / bins.length : plotW;
  const barWidth = Math.max(colWidth - 4, 1);

  const xLeft = (i: number): number => PAD.left + i * colWidth;
  const yOf = (v: number): number => PAD.top + (1 - v / yMax) * plotH;

  const yTicks = useMemo(() => {
    const n = 4;
    return Array.from({ length: n + 1 }, (_, i) => (yMax * i) / n);
  }, [yMax]);

  return (
    <svg
      className="sse-pct-hist"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="단일 일자 percentile 히스토그램"
    >
      {yTicks.map((t) => {
        const y = yOf(t);
        return (
          <line
            key={`g-${t}`}
            className="sse-pct-hist__grid"
            x1={PAD.left}
            x2={VIEW_W - PAD.right}
            y1={y}
            y2={y}
          />
        );
      })}
      {yTicks.map((t) => (
        <text
          key={`yt-${t}`}
          className="sse-pct-hist__tick"
          x={PAD.left - 6}
          y={yOf(t) + 3}
          textAnchor="end"
        >
          {Math.round(t)}
        </text>
      ))}

      <line
        className="sse-pct-hist__axis"
        x1={PAD.left}
        x2={VIEW_W - PAD.right}
        y1={PAD.top + plotH}
        y2={PAD.top + plotH}
      />
      <line
        className="sse-pct-hist__axis"
        x1={PAD.left}
        x2={PAD.left}
        y1={PAD.top}
        y2={PAD.top + plotH}
      />

      {bins.map((b, i) => {
        const x = xLeft(i) + (colWidth - barWidth) / 2;
        const y = yOf(b.count);
        const h = Math.max(PAD.top + plotH - y, 0);
        const pct = totalCount > 0 ? (b.count * 100) / totalCount : 0;
        return (
          <rect
            key={`bin-${i}`}
            x={x}
            y={y}
            width={barWidth}
            height={h}
            fill="var(--accent)"
            opacity={0.75}
          >
            <title>
              percentile {b.bin_start.toFixed(0)}% ~ {b.bin_end.toFixed(0)}% ·{' '}
              {b.count} 종목 ({pct.toFixed(1)}%)
            </title>
          </rect>
        );
      })}

      {/* x tick — bin start (0.0, 0.2, ..., 1.0). */}
      {bins.map((b, i) => {
        if (i % Math.max(Math.ceil(bins.length / 6), 1) !== 0 && i !== bins.length - 1) {
          return null;
        }
        const cx = xLeft(i) + colWidth / 2;
        return (
          <text
            key={`xt-${i}`}
            className="sse-pct-hist__tick"
            x={cx}
            y={VIEW_H - PAD.bottom + 14}
            textAnchor="middle"
          >
            {b.bin_start.toFixed(0)}
          </text>
        );
      })}
    </svg>
  );
});
